import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import logger from '@adc/logger'
import classnames from 'classnames/bind'
import Tab from '../../../layouts/tab'
import Icon from '../../../elementals/icon'
import MediaPanel, { View as MediaView } from './mediaPanel'
import { Map, Carousel, NotAvailable } from './mediaPanel/views'
import getStreetViewAvailability from '../../../../utils/getStreetViewAvailability'
import Pin from './pin'
import styles from './index.scss'

const cx = classnames.bind(styles)

const Overlay = ({
  config,
  onClose,
  addressTuple,
  images,
  coords,
  children,
  activeTabIndex,
  onTabChange,
  featureFlags,
  carouselRef,
}) => {
  const panes = []
  const hasValidCoords = !!coords?.lat && !!coords?.lng
  const elmId = 'pdp_photo'
  const [streetViewAvailable, setStreetViewAvailable] = useState(false)

  // Check Street View availability whenever coords change
  useEffect(() => {
    ;(async () => {
      if (coords) {
        try {
          const available = await getStreetViewAvailability(coords)
          setStreetViewAvailable(available)
        } catch (error) {
          logger.info(
            'Unhandled error in StreetView availability check:',
            error
          )
          setStreetViewAvailable(false)
        }
      }
    })().catch(error => {
      logger.info('Unhandled error in StreetView availability check:', error)
    })
  }, [coords])
  panes.push({
    menuItem: {
      disabled: images.length === 0,
      key: 'photos',
      'data-elm-id': 'photos_tab',
      content: (
        <>
          <Icon
            name='image'
            iconStyle='light'
            style={{ color: 'inherit' }}
            size='large'
          />
          <span>Photos</span>
        </>
      ),
    },
    render: () => (
      <Tab.Pane key='photos' className={cx('pane')}>
        <Carousel
          images={images}
          className={cx('carousel')}
          elmId={elmId}
          cls={{ left: cx('arr-prev'), right: cx('arr-next') }}
          arrowIconProps={{
            elmId,
          }}
          featureFlags={featureFlags}
          carouselRef={carouselRef}
        />
      </Tab.Pane>
    ),
  })

  panes.push({
    menuItem: {
      key: 'map',
      'data-elm-id': 'map_tab',
      content: (
        <>
          <Icon
            name='map-marked'
            iconStyle='light'
            style={{ color: 'inherit' }}
            size='large'
          />
          <span>Map View</span>
        </>
      ),
    },
    render: () => (
      <Tab.Pane key='map' className={cx('pane')}>
        {hasValidCoords ? (
          <Map
            {...coords}
            className={cx('map-view')}
            elmId='property_gallery_panel_map_view'
            apiKey={config.GOOGLE_MAP_CLIENT_ID}
            apiChannel={config.GOOGLE_MAP_CHANNEL}
          >
            <Pin {...coords} />
          </Map>
        ) : (
          <NotAvailable text='Map View Not Available' />
        )}
      </Tab.Pane>
    ),
  })
  // Conditionally if Street View is available
  if (streetViewAvailable) {
    panes.push({
      menuItem: {
        key: 'street-view',
        'data-elm-id': 'street_tab',
        content: (
          <>
            <Icon
              name='street-view'
              iconStyle='light'
              style={{ color: 'inherit' }}
              size='large'
            />
            <span>Street View</span>
          </>
        ),
      },
      render: () => (
        <Tab.Pane key='street-view' className={cx('pane')}>
          {hasValidCoords ? (
            <Map
              {...coords}
              streetview
              className={cx('street-view')}
              elmId='property_gallery_panel_street_view'
              apiKey={config.GOOGLE_MAP_CLIENT_ID}
              apiChannel={config.GOOGLE_MAP_CHANNEL}
            />
          ) : (
            <NotAvailable text='Street View Not Available' />
          )}
        </Tab.Pane>
      ),
    })
  }
  return (
    <MediaPanel
      elmId='property_gallery_panel'
      cls={{ close: cx('close') }}
      className={cx('root')}
      onClose={onClose}
    >
      <div className={cx('content')}>
        <div
          className={cx('address')}
          data-elm-id='property_gallery_panel_address'
        >
          <p data-elm-id='property_gallery_panel_address1'>
            {addressTuple[0]} <br />
            {addressTuple[1]}
          </p>
        </div>
        <MediaView>
          <Tab
            elmId='tab'
            panes={panes}
            activeIndex={activeTabIndex}
            onTabChange={(_e, { activeIndex }) => {
              onTabChange(activeIndex)
            }}
            role='secondary'
            collapsing={false}
            transparentMode
          />
          {children}
        </MediaView>
      </div>
    </MediaPanel>
  )
}

Overlay.propTypes = {
  addressTuple: PropTypes.arrayOf(PropTypes.string).isRequired,
  coords: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.element,
  activeTabIndex: PropTypes.number,
  onTabChange: PropTypes.func,
  featureFlags: PropTypes.object,
  carouselRef: PropTypes.object,
}

export default Overlay
